import {
    Table,
    Button,
    Modal,
    ModalDialog,
    ModalClose,
    DialogTitle,
    DialogContent,
    Typography,
    Chip,
    Stack,
    Divider,
    Select,
    Option,
    IconButton,
    Box,
    Input,
    Textarea,
    FormLabel,
    Tabs,
    TabList,
    Tab,
    TabPanel,
    Checkbox,
    FormControl,
    Sheet,
} from "@mui/joy";
import { Add, Close, PictureAsPdf } from "@mui/icons-material";
import { PageStandard } from "../utils/PageStandard";
import { useEffect, useState } from "react";
import {
    Preventivo,
    PreventivoRoute,
    PreventivoStatus,
    PreventivoTappa,
} from "../utils/Model";
import { getPreventivi, getPreventivo, updatePreventivo, approveEdit, rejectEdit } from "../utils/ViewModel";
import { formatDateTime, formatDateOnly } from "../utils/Utils";
import { downloadPreventivoPdf } from "../utils/PreventivoPdf";

export const STATUS_LABELS: Record<PreventivoStatus, string> = {
    [PreventivoStatus.bozza]: "Bozza",
    [PreventivoStatus.inviato]: "Inviato",
    [PreventivoStatus.in_lavorazione]: "In lavorazione",
    [PreventivoStatus.approvato]: "Approvato",
    [PreventivoStatus.rifiutato]: "Rifiutato",
    [PreventivoStatus.in_revisione]: "In revisione",
    [PreventivoStatus.annullato]: "Annullato",
};

const STATUS_COLORS: Record<PreventivoStatus, "neutral" | "primary" | "warning" | "success" | "danger"> = {
    [PreventivoStatus.bozza]: "neutral",
    [PreventivoStatus.inviato]: "primary",
    [PreventivoStatus.in_lavorazione]: "warning",
    [PreventivoStatus.approvato]: "success",
    [PreventivoStatus.rifiutato]: "danger",
    [PreventivoStatus.in_revisione]: "warning",
    [PreventivoStatus.annullato]: "neutral",
};

function emptyRoute(): PreventivoRoute {
    return { steps: [], pathChallenges: {} };
}

function emptyTappa(): PreventivoTappa {
    return {
        id: `custom_${Date.now()}`,
        type: "custom",
        name: "",
        coordinates: { lat: 0, lng: 0 },
    };
}

export function Preventivi() {
    const [preventivi, setPreventivi] = useState<Preventivo[]>([]);
    const [openId, setOpenId] = useState<string | null>(null);
    const [detail, setDetail] = useState<Preventivo | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editActionLoading, setEditActionLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    // Edit form state
    const [status, setStatus] = useState<PreventivoStatus>(PreventivoStatus.bozza);
    const [adminNote, setAdminNote] = useState("");
    const [additionalNotes, setAdditionalNotes] = useState("");
    const [totalPrice, setTotalPrice] = useState<number | "">("");
    const [breakdown, setBreakdown] = useState<{ label: string; amount: number }[]>([]);
    const [eventInfo, setEventInfo] = useState({
        participants: 0, event_date: "", zone: "", duration: "", time_slot: "", occasion: "",
    });
    const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "" });
    const [route, setRoute] = useState<PreventivoRoute>(emptyRoute());

    useEffect(() => {
        refresh();
    }, []);

    function refresh() {
        getPreventivi().then(setPreventivi).catch(() => setPreventivi([]));
    }

    useEffect(() => {
        if (!openId) { setDetail(null); return; }
        setDetailLoading(true);
        getPreventivo(openId).then((p) => {
            setDetail(p);
            if (p) {
                setStatus(p.status);
                setAdminNote(p.admin_note || "");
                setAdditionalNotes(p.additional_notes || "");
                setTotalPrice(p.total_price ?? "");
                setBreakdown(p.breakdown || []);
                setEventInfo({
                    participants: p.participants,
                    event_date: p.event_date || "",
                    zone: p.zone || "",
                    duration: p.duration || "",
                    time_slot: p.time_slot || "",
                    occasion: p.occasion || "",
                });
                setContact({
                    first_name: p.first_name || "",
                    last_name: p.last_name || "",
                    email: p.email || "",
                    phone: p.phone || "",
                });
                setRoute(p.route || emptyRoute());
            }
            setDetailLoading(false);
        });
    }, [openId]);

    async function handleSave() {
        if (!openId) return;
        setSaving(true);
        const ok = await updatePreventivo(openId, {
            status,
            admin_note: adminNote,
            additional_notes: additionalNotes || null,
            total_price: totalPrice === "" ? null : Number(totalPrice),
            breakdown,
            participants: eventInfo.participants,
            event_date: eventInfo.event_date || null,
            zone: eventInfo.zone || null,
            duration: eventInfo.duration || null,
            time_slot: eventInfo.time_slot || null,
            occasion: eventInfo.occasion || null,
            first_name: contact.first_name || null,
            last_name: contact.last_name || null,
            email: contact.email || null,
            phone: contact.phone || null,
            route,
        });
        setSaving(false);
        if (ok) {
            refresh();
            setOpenId(null);
        }
    }

    async function handleApproveEdit() {
        if (!openId) return;
        setEditActionLoading(true);
        const ok = await approveEdit(openId);
        setEditActionLoading(false);
        if (ok) { refresh(); setOpenId(null); }
    }

    async function handleRejectEdit() {
        if (!openId) return;
        const note = window.prompt("Nota per il cliente (opzionale):") || undefined;
        setEditActionLoading(true);
        const ok = await rejectEdit(openId, note);
        setEditActionLoading(false);
        if (ok) { refresh(); setOpenId(null); }
    }

    async function handleDownloadPdf(id: string, preloaded?: Preventivo | null) {
        setDownloadingId(id);
        try {
            const full = preloaded && preloaded.id === id ? preloaded : await getPreventivo(id);
            if (full) await downloadPreventivoPdf(full);
        } catch (err: any) {
            alert(`Errore nella generazione del PDF: ${err.message || err}`);
        }
        setDownloadingId(null);
    }

    function updateStage(key: "startLocation" | "endLocation" | number, updater: (t: PreventivoTappa) => PreventivoTappa) {
        setRoute((r) => {
            if (key === "startLocation") return { ...r, startLocation: r.startLocation ? updater(r.startLocation) : r.startLocation };
            if (key === "endLocation") return { ...r, endLocation: r.endLocation ? updater(r.endLocation) : r.endLocation };
            return { ...r, steps: r.steps.map((s, i) => (i === key ? updater(s) : s)) };
        });
    }

    return (
        <PageStandard title="Preventivi" sidebarVisible={true}>
            <Table aria-label="Preventivi" size="md" stickyHeader>
                <thead>
                    <tr>
                        <th style={{ width: "20%" }}>Cliente</th>
                        <th style={{ width: "12%" }}>Stato</th>
                        <th style={{ width: "10%" }}>Partecipanti</th>
                        <th style={{ width: "13%" }}>Data evento</th>
                        <th style={{ width: "13%" }}>Prezzo</th>
                        <th style={{ width: "17%" }}>Creato il</th>
                        <th style={{ width: "15%" }}>Azioni</th>
                    </tr>
                </thead>
                <tbody>
                    {preventivi.map((p) => (
                        <tr key={p.id}>
                            <td>
                                <Typography level="title-sm">{p.first_name} {p.last_name}</Typography>
                                <Typography level="body-xs" color="neutral">{p.email}</Typography>
                            </td>
                            <td><Chip size="sm" color={STATUS_COLORS[p.status]}>{STATUS_LABELS[p.status]}</Chip></td>
                            <td><Typography level="body-sm">{p.participants}</Typography></td>
                            <td><Typography level="body-sm">{formatDateOnly(p.event_date) || "—"}</Typography></td>
                            <td><Typography level="body-sm">{p.total_price != null ? `${p.total_price}€` : "—"}</Typography></td>
                            <td><Typography level="body-xs" color="neutral">{formatDateTime(p.created_at)}</Typography></td>
                            <td>
                                <Stack direction="row" spacing={1}>
                                    <Button size="sm" variant="soft" onClick={() => setOpenId(p.id)}>Gestisci</Button>
                                    <IconButton
                                        size="sm"
                                        variant="plain"
                                        loading={downloadingId === p.id}
                                        onClick={() => handleDownloadPdf(p.id)}
                                        title="Scarica PDF"
                                    >
                                        <PictureAsPdf sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal open={!!openId} onClose={() => setOpenId(null)}>
                <ModalDialog layout="center" size="lg" sx={{ width: "min(760px, 95vw)", maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <ModalClose />
                    <DialogTitle sx={{ pb: 0, pr: 5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                            <Box>
                                {detail?.first_name} {detail?.last_name}
                                <Typography component="span" level="body-sm" color="neutral" sx={{ ml: 1 }}>
                                    {detail?.email}
                                </Typography>
                            </Box>
                            {detail && (
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    loading={downloadingId === detail.id}
                                    onClick={() => handleDownloadPdf(detail.id, detail)}
                                    title="Scarica PDF"
                                >
                                    <PictureAsPdf sx={{ fontSize: 18 }} />
                                </IconButton>
                            )}
                        </Stack>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 1, overflow: "auto" }}>
                        {detailLoading || !detail ? (
                            <Typography level="body-sm" color="neutral">Caricamento…</Typography>
                        ) : (
                            <>
                                {detail.status === PreventivoStatus.in_revisione && detail.pendingPayload && (
                                    <Sheet variant="soft" color="warning" sx={{ p: 1.5, borderRadius: "sm", mb: 2 }}>
                                        <Typography level="title-sm" sx={{ mb: 0.5 }}>Modifica in attesa di approvazione</Typography>
                                        <Typography level="body-sm" sx={{ mb: 1 }}>
                                            Il cliente ha proposto una modifica. I dati mostrati nelle schede sotto
                                            sono ancora quelli attuali (approvati); la modifica proposta è disponibile
                                            nello storico stato più sotto.
                                        </Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="sm" color="success" loading={editActionLoading} onClick={handleApproveEdit}>
                                                Approva modifica
                                            </Button>
                                            <Button size="sm" color="danger" variant="soft" loading={editActionLoading} onClick={handleRejectEdit}>
                                                Rifiuta modifica
                                            </Button>
                                        </Stack>
                                    </Sheet>
                                )}
                                <Tabs defaultValue="dettagli" size="sm">
                                    <TabList sx={{ mb: 2 }}>
                                        <Tab value="dettagli">Dettagli evento</Tab>
                                        <Tab value="contatto">Contatto</Tab>
                                        <Tab value="percorso">Percorso</Tab>
                                        <Tab value="prezzo">Prezzo</Tab>
                                        <Tab value="cronologia">Cronologia</Tab>
                                    </TabList>

                                <TabPanel value="dettagli" sx={{ p: 0 }}>
                                    <Stack spacing={1.5}>
                                        <FormControl>
                                            <FormLabel>Partecipanti</FormLabel>
                                            <Input
                                                type="number" size="sm" value={eventInfo.participants}
                                                onChange={(e) => setEventInfo((v) => ({ ...v, participants: Number(e.target.value) || 0 }))}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Data evento</FormLabel>
                                            <Input
                                                type="date" size="sm" value={eventInfo.event_date}
                                                onChange={(e) => setEventInfo((v) => ({ ...v, event_date: e.target.value }))}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Zona</FormLabel>
                                            <Input size="sm" value={eventInfo.zone} onChange={(e) => setEventInfo((v) => ({ ...v, zone: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Durata</FormLabel>
                                            <Input size="sm" value={eventInfo.duration} onChange={(e) => setEventInfo((v) => ({ ...v, duration: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Fascia oraria</FormLabel>
                                            <Input size="sm" value={eventInfo.time_slot} onChange={(e) => setEventInfo((v) => ({ ...v, time_slot: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Occasione</FormLabel>
                                            <Input size="sm" value={eventInfo.occasion} onChange={(e) => setEventInfo((v) => ({ ...v, occasion: e.target.value }))} />
                                        </FormControl>
                                        <Divider />
                                        <FormControl>
                                            <FormLabel>Stato</FormLabel>
                                            <Select size="sm" value={status} onChange={(_, v) => v && setStatus(v)}>
                                                {Object.values(PreventivoStatus).map((s) => (
                                                    <Option key={s} value={s}>{STATUS_LABELS[s]}</Option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Nota interna</FormLabel>
                                            <Textarea minRows={2} value={adminNote} onChange={(e) => setAdminNote(e.target.value)} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Note aggiuntive (dal cliente)</FormLabel>
                                            <Textarea
                                                minRows={2} value={additionalNotes}
                                                onChange={(e) => setAdditionalNotes(e.target.value)}
                                                placeholder="Richieste particolari lasciate dal cliente nel wizard"
                                            />
                                        </FormControl>
                                    </Stack>
                                </TabPanel>

                                <TabPanel value="contatto" sx={{ p: 0 }}>
                                    <Stack spacing={1.5}>
                                        <FormControl>
                                            <FormLabel>Nome</FormLabel>
                                            <Input size="sm" value={contact.first_name} onChange={(e) => setContact((v) => ({ ...v, first_name: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Cognome</FormLabel>
                                            <Input size="sm" value={contact.last_name} onChange={(e) => setContact((v) => ({ ...v, last_name: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Email</FormLabel>
                                            <Input size="sm" value={contact.email} onChange={(e) => setContact((v) => ({ ...v, email: e.target.value }))} />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel>Telefono</FormLabel>
                                            <Input size="sm" value={contact.phone} onChange={(e) => setContact((v) => ({ ...v, phone: e.target.value }))} />
                                        </FormControl>
                                    </Stack>
                                </TabPanel>

                                <TabPanel value="percorso" sx={{ p: 0 }}>
                                    <Stack spacing={2}>
                                        <TappaCard
                                            label="Partenza"
                                            tappa={route.startLocation}
                                            onChange={(t) => updateStage("startLocation", () => t)}
                                            onRemove={() => setRoute((r) => ({ ...r, startLocation: undefined }))}
                                            onAdd={() => setRoute((r) => ({ ...r, startLocation: emptyTappa() }))}
                                        />
                                        {route.steps.map((s, i) => (
                                            <TappaCard
                                                key={s.id}
                                                label={`Tappa ${i + 1}`}
                                                tappa={s}
                                                onChange={(t) => updateStage(i, () => t)}
                                                onRemove={() => setRoute((r) => ({ ...r, steps: r.steps.filter((_, idx) => idx !== i) }))}
                                            />
                                        ))}
                                        <Button
                                            size="sm" variant="soft" startDecorator={<Add />}
                                            onClick={() => setRoute((r) => ({ ...r, steps: [...r.steps, emptyTappa()] }))}
                                        >
                                            Aggiungi tappa
                                        </Button>
                                        <TappaCard
                                            label="Arrivo"
                                            tappa={route.endLocation}
                                            onChange={(t) => updateStage("endLocation", () => t)}
                                            onRemove={() => setRoute((r) => ({ ...r, endLocation: undefined }))}
                                            onAdd={() => setRoute((r) => ({ ...r, endLocation: emptyTappa() }))}
                                            isEndLocation
                                        />
                                    </Stack>
                                </TabPanel>

                                <TabPanel value="prezzo" sx={{ p: 0 }}>
                                    <Stack spacing={1.5}>
                                        <FormControl>
                                            <FormLabel>Prezzo totale (€)</FormLabel>
                                            <Input
                                                type="number" size="sm" value={totalPrice}
                                                onChange={(e) => setTotalPrice(e.target.value === "" ? "" : Number(e.target.value))}
                                            />
                                        </FormControl>
                                        <Divider />
                                        <Typography level="title-sm">Dettaglio voci</Typography>
                                        {breakdown.map((item, i) => (
                                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                                <Input
                                                    size="sm" placeholder="Descrizione" value={item.label} sx={{ flex: 1 }}
                                                    onChange={(e) => setBreakdown((b) => b.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))}
                                                />
                                                <Input
                                                    size="sm" type="number" placeholder="€" value={item.amount} sx={{ width: 100 }}
                                                    onChange={(e) => setBreakdown((b) => b.map((x, idx) => (idx === i ? { ...x, amount: Number(e.target.value) || 0 } : x)))}
                                                />
                                                <IconButton size="sm" variant="plain" color="danger" onClick={() => setBreakdown((b) => b.filter((_, idx) => idx !== i))}>
                                                    <Close sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Stack>
                                        ))}
                                        <Button
                                            size="sm" variant="soft" startDecorator={<Add />}
                                            onClick={() => setBreakdown((b) => [...b, { label: "", amount: 0 }])}
                                        >
                                            Aggiungi voce
                                        </Button>
                                    </Stack>
                                </TabPanel>

                                <TabPanel value="cronologia" sx={{ p: 0 }}>
                                    <Stack spacing={1.5}>
                                        {!detail.statusHistory?.length && (
                                            <Typography level="body-sm" color="neutral">Nessuno storico disponibile.</Typography>
                                        )}
                                        {detail.statusHistory?.map((h) => (
                                            <Sheet key={h.id} variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Chip size="sm" color={STATUS_COLORS[h.status]}>{STATUS_LABELS[h.status]}</Chip>
                                                    <Typography level="body-xs" color="neutral">{formatDateTime(h.changed_at)}</Typography>
                                                </Stack>
                                                {h.note && <Typography level="body-sm" sx={{ mt: 0.5 }}>{h.note}</Typography>}
                                            </Sheet>
                                        ))}
                                    </Stack>
                                </TabPanel>
                            </Tabs>
                            </>
                        )}
                    </DialogContent>
                    <Box sx={{ p: 2, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
                        <Button size="sm" loading={saving} onClick={handleSave} disabled={!detail}>
                            Salva
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </PageStandard>
    );
}

function TappaCard(props: {
    label: string;
    tappa?: PreventivoTappa;
    onChange: (t: PreventivoTappa) => void;
    onRemove: () => void;
    onAdd?: () => void;
    isEndLocation?: boolean;
}) {
    const { label, tappa, onChange, onRemove, onAdd, isEndLocation } = props;

    if (!tappa) {
        return (
            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: "sm" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="body-sm" color="neutral">{label}: nessuna tappa</Typography>
                    {onAdd && <Button size="sm" variant="plain" startDecorator={<Add />} onClick={onAdd}>Aggiungi</Button>}
                </Stack>
            </Sheet>
        );
    }

    const gamification = tappa.gamification || {};
    const consumazioni = tappa.consumazioni;
    const prize = tappa.prize;

    return (
        <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: "sm" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography level="title-sm">{label}</Typography>
                <IconButton size="sm" variant="plain" color="danger" onClick={onRemove}>
                    <Close sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>
            <Stack spacing={1}>
                <Stack direction="row" spacing={1}>
                    <Input
                        size="sm" placeholder="Nome tappa" value={tappa.name} sx={{ flex: 1 }}
                        onChange={(e) => onChange({ ...tappa, name: e.target.value })}
                    />
                    <Select
                        size="sm" value={tappa.type}
                        onChange={(_, v) => v && onChange({ ...tappa, type: v as any })}
                        sx={{ width: 120 }}
                    >
                        <Option value="venue">Locale</Option>
                        <Option value="poi">Punto di interesse</Option>
                        <Option value="custom">Personalizzata</Option>
                    </Select>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Input
                        size="sm" type="number" placeholder="Lat" value={tappa.coordinates.lat} sx={{ flex: 1 }}
                        onChange={(e) => onChange({ ...tappa, coordinates: { ...tappa.coordinates, lat: Number(e.target.value) || 0 } })}
                    />
                    <Input
                        size="sm" type="number" placeholder="Lng" value={tappa.coordinates.lng} sx={{ flex: 1 }}
                        onChange={(e) => onChange({ ...tappa, coordinates: { ...tappa.coordinates, lng: Number(e.target.value) || 0 } })}
                    />
                </Stack>

                {tappa.type === "venue" && (
                    <Stack spacing={0.5} sx={{ pl: 1, borderLeft: "2px solid", borderColor: "divider" }}>
                        <FormControl orientation="horizontal" sx={{ justifyContent: "space-between" }}>
                            <FormLabel>Consumazioni abilitate</FormLabel>
                            <Checkbox
                                checked={!!consumazioni?.enabled}
                                onChange={(e) => onChange({ ...tappa, consumazioni: { ...(consumazioni || { enabled: false }), enabled: e.target.checked } })}
                            />
                        </FormControl>
                        <Stack direction="row" spacing={1}>
                            <Input
                                size="sm" placeholder="Variante drink" value={consumazioni?.drinkVariant || ""} sx={{ flex: 1 }}
                                onChange={(e) => onChange({ ...tappa, consumazioni: { ...(consumazioni || { enabled: true }), drinkVariant: e.target.value } })}
                            />
                            <Input
                                size="sm" placeholder="Variante cibo" value={consumazioni?.foodVariant || ""} sx={{ flex: 1 }}
                                onChange={(e) => onChange({ ...tappa, consumazioni: { ...(consumazioni || { enabled: true }), foodVariant: e.target.value } })}
                            />
                        </Stack>
                        <Input
                            size="sm" placeholder="Giochi selezionati (separati da virgola)"
                            value={(gamification.selectedGames || []).join(", ")}
                            onChange={(e) => onChange({
                                ...tappa,
                                gamification: { ...gamification, selectedGames: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) },
                            })}
                        />
                    </Stack>
                )}

                {tappa.type === "venue" && isEndLocation && (
                    <Stack spacing={0.5} sx={{ pl: 1, borderLeft: "2px solid", borderColor: "warning.400" }}>
                        <FormControl orientation="horizontal" sx={{ justifyContent: "space-between" }}>
                            <FormLabel>🏆 Premio vincitore abilitato</FormLabel>
                            <Checkbox
                                checked={!!prize?.enabled}
                                onChange={(e) => onChange({ ...tappa, prize: { ...(prize || { enabled: false }), enabled: e.target.checked } })}
                            />
                        </FormControl>
                        <Stack direction="row" spacing={1}>
                            <Input
                                size="sm" placeholder="Variante drink" value={prize?.drinkVariant || ""} sx={{ flex: 1 }}
                                onChange={(e) => onChange({ ...tappa, prize: { ...(prize || { enabled: true }), drinkVariant: e.target.value } })}
                            />
                            <Input
                                size="sm" placeholder="Variante cibo" value={prize?.foodVariant || ""} sx={{ flex: 1 }}
                                onChange={(e) => onChange({ ...tappa, prize: { ...(prize || { enabled: true }), foodVariant: e.target.value } })}
                            />
                        </Stack>
                    </Stack>
                )}

                <Stack direction="row" spacing={2} sx={{ pl: 1 }}>
                    <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                        <FormLabel>QR Code</FormLabel>
                        <Checkbox
                            checked={!!gamification.qrCode}
                            onChange={(e) => onChange({ ...tappa, gamification: { ...gamification, qrCode: e.target.checked } })}
                        />
                    </FormControl>
                    <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                        <FormLabel>Quiz</FormLabel>
                        <Checkbox
                            checked={!!gamification.quiz}
                            onChange={(e) => onChange({ ...tappa, gamification: { ...gamification, quiz: e.target.checked } })}
                        />
                    </FormControl>
                </Stack>
                {gamification.quiz && (
                    <Input
                        size="sm" placeholder="Descrizione quiz" value={gamification.quizDescription || ""}
                        onChange={(e) => onChange({ ...tappa, gamification: { ...gamification, quizDescription: e.target.value } })}
                    />
                )}
            </Stack>
        </Sheet>
    );
}
